import { useState, useEffect, useRef } from 'react'

interface Notification {
  id: string
  type: 'info' | 'success' | 'warning' | 'error'
  title: string
  message: string
  time: number
  read: boolean
}

// Global notification store
let notifications: Notification[] = []
let listeners: (() => void)[] = []

export function addNotification(type: Notification['type'], title: string, message: string) {
  notifications = [{
    id: crypto.randomUUID(), type, title, message, time: Date.now(), read: false,
  }, ...notifications].slice(0, 50)
  listeners.forEach((l) => l())
}

function useNotifications() {
  const [, setTick] = useState(0)
  useEffect(() => {
    const listener = () => setTick((t) => t + 1)
    listeners.push(listener)
    return () => { listeners = listeners.filter((l) => l !== listener) }
  }, [])
  return notifications
}

export function NotificationBell() {
  const notifs = useNotifications()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const unread = notifs.filter((n) => !n.read).length

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const markAllRead = () => {
    notifications = notifications.map((n) => ({ ...n, read: true }))
    listeners.forEach((l) => l())
  }

  const typeIcon: Record<string, string> = {
    info: '\u2139\ufe0f', success: '\u2705', warning: '\u26a0\ufe0f', error: '\u274c',
  }

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <button
        className="titlebar__icon-btn"
        onClick={() => { setOpen((o) => !o); if (!open) markAllRead() }}
        title="Notifications"
        style={{ position: 'relative' }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2, width: 8, height: 8,
            borderRadius: '50%', background: 'var(--red)', border: '1.5px solid var(--bg-1)',
          }} />
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 200,
          background: 'var(--bg-1)', border: '1px solid var(--border-2)',
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          width: 300, maxHeight: 360, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-0)', display: 'flex', justifyContent: 'space-between' }}>
            <span>Notifications</span>
            {notifs.length > 0 && (
              <button onClick={() => { notifications = []; listeners.forEach((l) => l()) }} style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 9, fontFamily: 'var(--font-mono)' }}>Clear all</button>
            )}
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {notifs.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>No notifications</div>
            ) : notifs.map((n) => (
              <div key={n.id} style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)', opacity: n.read ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ fontSize: 11 }}>{typeIcon[n.type]}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-0)', fontFamily: 'var(--font-mono)' }}>{n.title}</span>
                  <span style={{ fontSize: 8, color: 'var(--text-2)', marginLeft: 'auto', fontFamily: 'var(--font-mono)' }}>
                    {Math.round((Date.now() - n.time) / 60000)}m ago
                  </span>
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>{n.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
